import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { HipSpinePostureApiObject } from '@/api/hip-spine-study/hipSpinePosture';
import type { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import type { useHipSpineSyncStore } from '@/views/3d-planner/hipSpine/hipSpineSyncStore';
import type { useHipSpinePostureSavePlan } from '@/views/3d-planner/hipSpine/useHipSpinePostureSavePlan';
import type { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
import type { HipSpinePostureViewState } from '@/views/3d-planner/hipSpine/types';

/** Approval-success banner duration before reverting the button to its idle state. */
export const APPROVE_SUCCESS_MS = 1500;

export const POSTURE_TABS = [
    { label: 'Standing Sagittal', value: 'standing-sagittal' },
    { label: 'Flexed Seated Sagittal', value: 'flexed-seated-sagittal' },
    { label: 'Lumbar Sagittal', value: 'lumbar-sagittal' },
] as const;

/**
 * Shared context the modal's gating computeds and actions operate on: the sibling
 * stores it orchestrates, the in-flight Approve/Reset flags, and the two
 * "current selection" derived refs everything keys off.
 */
export type HipSpineXRayModalContext = {
    store: ReturnType<typeof useHipSpineStore>;
    sync: ReturnType<typeof useHipSpineSyncStore>;
    savePlan: ReturnType<typeof useHipSpinePostureSavePlan>;
    editVm: ReturnType<typeof useHipSpineXRayEditViewModel>;
    // In-flight flags for Approve and Reset. Declared together so each operation's
    // "disabled" computed can gate on the other and serialise the two — both mutate the
    // active posture's server state, so they must never overlap.
    isResetting: Ref<boolean>;
    resetError: Ref<string | null>;
    isApproving: Ref<boolean>;
    approveSuccess: Ref<boolean>;
    approveError: Ref<string | null>;
    activePosture: ComputedRef<HipSpinePostureApiObject | undefined>;
    activeViewState: ComputedRef<HipSpinePostureViewState | undefined>;
};

export function createModalState(
    store: HipSpineXRayModalContext['store'],
    sync: HipSpineXRayModalContext['sync'],
    savePlan: HipSpineXRayModalContext['savePlan'],
    editVm: HipSpineXRayModalContext['editVm'],
): HipSpineXRayModalContext {
    const activePosture = computed(() =>
        store.activePostureId
            ? store.hipSpinePostures.find((candidate) => candidate.posture === store.activePostureId)
            : undefined,
    );
    const activeViewState = computed(() =>
        store.activePostureId ? store.viewState(store.activePostureId) : undefined,
    );
    return {
        store,
        sync,
        savePlan,
        editVm,
        isResetting: ref(false),
        resetError: ref<string | null>(null),
        isApproving: ref(false),
        approveSuccess: ref(false),
        approveError: ref<string | null>(null),
        activePosture,
        activeViewState,
    };
}
